import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  LogOut,
  Loader2,
  Users,
  MessageSquare,
  ShoppingCart,
  DollarSign,
  Mail,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminLogin,
  adminStats,
  adminNewsletter,
  adminContacts,
  adminOrders,
  downloadSubscribersCsv,
} from "../lib/api";

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      localStorage.setItem("mu_token", data.access_token);
      toast.success("Welcome back, " + data.name);
      onSuccess();
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5 grain">
      <div className="light-bleed h-[400px] w-[400px] top-10 right-10 opacity-30" />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-sm rounded-3xl border border-white/10 bg-charcoal p-8"
        data-testid="admin-login-form"
      >
        <img src="/logo-red.png" alt="Motivation Unlocked" className="h-16 w-auto mx-auto mb-6" />
        <h1 className="font-heading font-800 text-2xl text-center">Admin Access</h1>
        <p className="text-center text-white/50 text-sm mt-2 mb-8">
          Command center for the movement.
        </p>
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent border-b border-white/20 py-3 px-1 mb-5 text-white placeholder-white/30 focus:border-blaze focus:outline-none"
          data-testid="admin-email-input"
        />
        <input
          type="password"
          required
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border-b border-white/20 py-3 px-1 mb-8 text-white placeholder-white/30 focus:border-blaze focus:outline-none"
          data-testid="admin-password-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blaze px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02] hover:blaze-glow disabled:opacity-50"
          data-testid="admin-login-btn"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
        <a href="/" className="block text-center text-xs text-white/40 mt-6 hover:text-white">
          ← Back to site
        </a>
      </motion.form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-charcoal p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="grid place-items-center h-10 w-10 rounded-xl bg-blaze/10 text-blaze">
        <Icon size={18} />
      </span>
    </div>
    <div className="mt-4 font-heading font-800 text-3xl">{value}</div>
    <div className="overline text-white/40 mt-1">{label}</div>
  </div>
);

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("subscribers");
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [s, n, c, o] = await Promise.all([
        adminStats(),
        adminNewsletter(),
        adminContacts(),
        adminOrders(),
      ]);
      setStats(s);
      setSubs(n);
      setMsgs(c);
      setOrders(o);
    } catch {
      toast.error("Session expired. Please sign in again.");
      onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-blaze" size={30} />
      </div>
    );
  }

  const tabs = [
    { id: "subscribers", label: `Subscribers (${subs.length})` },
    { id: "messages", label: `Messages (${msgs.length})` },
    { id: "orders", label: `Orders (${orders.length})` },
  ];

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const exportCsv = async () => {
    try {
      const blob = await downloadSubscribersCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "subscribers.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Subscribers exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen grain">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src="/logo-red.png" alt="Motivation Unlocked" className="h-9 w-auto" />
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
            data-testid="admin-logout-btn"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-heading font-800 text-3xl mb-8">Dashboard</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={DollarSign} label="Revenue (paid)" value={`$${stats.revenue}`} testid="stat-revenue" />
          <StatCard icon={ShoppingCart} label={`Orders · ${stats.paid_orders} paid`} value={stats.orders} testid="stat-orders" />
          <StatCard icon={Users} label="Subscribers" value={stats.subscribers} testid="stat-subscribers" />
          <StatCard icon={MessageSquare} label="Messages" value={stats.messages} testid="stat-messages" />
        </div>

        <div className="flex gap-2 mb-6 flex-wrap items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-blaze text-white" : "border border-white/20 text-white/60 hover:bg-white/10"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === "subscribers" && (
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
              data-testid="admin-export-csv"
            >
              <Download size={15} /> Export CSV
            </button>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-charcoal overflow-hidden">
          {tab === "subscribers" && (
            <table className="w-full text-sm" data-testid="subscribers-table">
              <thead>
                <tr className="text-left text-white/40 border-b border-white/10">
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && (
                  <tr><td colSpan={2} className="p-6 text-center text-white/40">No subscribers yet.</td></tr>
                )}
                {subs.map((s, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 flex items-center gap-2"><Mail size={14} className="text-blaze" />{s.email}</td>
                    <td className="p-4 text-white/50">{fmtDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "messages" && (
            <div className="divide-y divide-white/5" data-testid="messages-list">
              {msgs.length === 0 && <p className="p-6 text-center text-white/40">No messages yet.</p>}
              {msgs.map((m, i) => (
                <div key={i} className="p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-heading font-700">{m.name}
                      <span className="ml-2 text-xs text-white/40 font-body">{m.email}</span>
                    </div>
                    <span className="rounded-full border border-blaze/40 text-blaze overline px-3 py-1">{m.topic}</span>
                  </div>
                  <p className="mt-3 text-white/70 text-sm leading-relaxed">{m.message}</p>
                  <p className="mt-2 text-xs text-white/40">{fmtDate(m.created_at)}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "orders" && (
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="text-left text-white/40 border-b border-white/10">
                  <th className="p-4 font-medium">Items</th>
                  <th className="p-4 font-medium">Amount</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-white/40">No orders yet.</td></tr>
                )}
                {orders.map((o, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4">{o.items_summary}</td>
                    <td className="p-4 font-mono">${(o.amount || 0).toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        o.payment_status === "paid" ? "bg-green-500/15 text-green-400" : "bg-white/10 text-white/50"
                      }`}>{o.payment_status}</span>
                    </td>
                    <td className="p-4 text-white/50">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("mu_token"));

  const logout = () => {
    localStorage.removeItem("mu_token");
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
